import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  TrendingUp, TrendingDown, DollarSign, Receipt, FileText, Wallet, Calculator,
  Plus, Edit2, Trash2, Download, FileDown, Save, RefreshCw, Calendar, Search,
  ArrowUpRight, ArrowDownRight, Upload, Eye, CheckCircle2, AlertCircle,
  ChevronRight, X, BarChart3, PieChart, Settings, BookOpen,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtMoneySign = (n) => `${n < 0 ? '-' : ''}$${Math.abs(Number(n || 0)).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PERIOD_PRESETS = [
  { value: 'current_week', label: 'This Week' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'current_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'current_quarter', label: 'This Quarter' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'current_year', label: 'This Year' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'all_time', label: 'All Time' },
  { value: 'custom', label: 'Custom Range…' },
];

// Period selector — reusable across tabs
const PeriodPicker = ({ period, setPeriod, customStart, setCustomStart, customEnd, setCustomEnd, label }) => (
  <div className="flex flex-wrap items-center gap-2">
    <Calendar className="w-4 h-4 text-[#6B6B7A]" />
    <select
      value={period}
      onChange={(e) => setPeriod(e.target.value)}
      className="h-9 rounded-xl border border-[#E8E5DF] text-sm bg-white px-3 font-medium"
      data-testid="acct-period-select"
    >
      {PERIOD_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
    </select>
    {period === 'custom' && (
      <>
        <Input
          type="date"
          value={customStart}
          onChange={(e) => setCustomStart(e.target.value)}
          className="h-9 rounded-xl border-[#E8E5DF] text-sm w-36"
          data-testid="acct-custom-start"
        />
        <span className="text-xs text-[#6B6B7A]">to</span>
        <Input
          type="date"
          value={customEnd}
          onChange={(e) => setCustomEnd(e.target.value)}
          className="h-9 rounded-xl border-[#E8E5DF] text-sm w-36"
          data-testid="acct-custom-end"
        />
      </>
    )}
    {label && <span className="text-xs text-[#6B6B7A] ml-1">· {label}</span>}
  </div>
);

const SummaryCard = ({ icon: Icon, label, value, sub, color = '#FF3B6B', testId, trend, onDrillDown }) => {
  const clickable = typeof onDrillDown === 'function';
  const Tag = clickable ? 'button' : 'div';
  return (
    <Tag
      type={clickable ? 'button' : undefined}
      onClick={clickable ? onDrillDown : undefined}
      className={`bg-white rounded-2xl border border-[#E8E5DF] p-4 transition-shadow text-left w-full ${clickable ? 'hover:shadow-md hover:border-[#FF3B6B] cursor-pointer' : 'hover:shadow-md'}`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-2">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${color}1A` }}
        >
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        {trend !== undefined && (
          <Badge
            variant="outline"
            className={`text-[10px] px-1.5 py-0 ${trend >= 0 ? 'border-green-300 text-green-700 bg-green-50' : 'border-red-300 text-red-700 bg-red-50'}`}
          >
            {trend >= 0 ? <ArrowUpRight className="w-3 h-3 inline" /> : <ArrowDownRight className="w-3 h-3 inline" />}
            {Math.abs(trend).toFixed(1)}%
          </Badge>
        )}
      </div>
      <div className="text-[11px] uppercase tracking-wider text-[#6B6B7A] font-semibold mb-1 flex items-center gap-1">
        {label}
        {clickable && <ChevronRight className="w-3 h-3 opacity-40" />}
      </div>
      <div className="text-2xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>{value}</div>
      {sub && <div className="text-[11px] text-[#6B6B7A] mt-1">{sub}</div>}
    </Tag>
  );
};

// ────────  Reconciliation Health (iter-133)  ────────
const HEALTH_STATUS_COLORS = {
  healthy: { bg: '#0FAB66', label: 'Healthy', ring: '#0FAB66' },
  warning: { bg: '#FF7A2E', label: 'Warning', ring: '#FF7A2E' },
  critical: { bg: '#D4302A', label: 'Critical', ring: '#D4302A' },
};

const ReconciliationHealthCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/accounting/reconciliation-health`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load reconciliation health');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const palette = HEALTH_STATUS_COLORS[data?.status] || HEALTH_STATUS_COLORS.healthy;
  const score = data?.score ?? 100;

  const runRemirror = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/accounting/reconciliation/remirror-stripe`, {}, { withCredentials: true });
      toast.success(`Re-mirror complete — mirrored ${res.data.mirrored}, skipped ${res.data.skipped}`);
      await fetchHealth();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Re-mirror failed');
    } finally { setBusy(false); }
  };

  const runRecomputeOne = async (bookingId) => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/admin/accounting/reconciliation/recompute-balance/${bookingId}`, {}, { withCredentials: true });
      toast.success(`Recomputed ${bookingId}`);
      await fetchHealth();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recompute failed');
    } finally { setBusy(false); }
  };

  const runRecomputeAll = async () => {
    if (!window.confirm('Recompute balance for every active booking? This is safe and idempotent.')) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/accounting/reconciliation/recompute-balance-all`, {}, { withCredentials: true });
      toast.success(`Recomputed ${res.data.recomputed} bookings (${res.data.failed} failed)`);
      await fetchHealth();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk recompute failed');
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5" data-testid="recon-health-card">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>
              Reconciliation Health
            </h3>
            <p className="text-xs text-[#6B6B7A] mt-0.5">
              One number to trust before sending HST returns.
              {data?.checked_at && ` Checked ${new Date(data.checked_at).toLocaleTimeString()}.`}
            </p>
          </div>
          <Button onClick={fetchHealth} disabled={loading || busy} variant="outline" size="sm"
            className="rounded-full text-xs border-[#E8E5DF]" data-testid="recon-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
          {/* Health score */}
          <div className="flex items-center gap-4">
            <div
              className="relative flex items-center justify-center rounded-full"
              style={{
                width: 96, height: 96,
                background: `conic-gradient(${palette.ring} ${score * 3.6}deg, #F1ECE1 0)`,
              }}
              data-testid="recon-score-ring"
            >
              <div className="bg-white rounded-full flex flex-col items-center justify-center" style={{ width: 76, height: 76 }}>
                <div className="text-2xl font-bold text-[#1E1E24]" data-testid="recon-score-value">{score}</div>
                <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: palette.bg }}>
                  {palette.label}
                </div>
              </div>
            </div>
          </div>

          {/* 3 mini stats */}
          {(data?.audits || []).map((a) => (
            <button
              key={a.name}
              onClick={() => setOpen(true)}
              className="text-left p-3 rounded-xl border border-[#E8E5DF] hover:border-[#FF3B6B] hover:shadow-md transition"
              data-testid={`recon-audit-${a.name.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <div className="text-[11px] uppercase tracking-wider text-[#6B6B7A] font-semibold">{a.name}</div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className={`text-2xl font-bold ${a.count === 0 ? 'text-[#0FAB66]' : 'text-[#D4302A]'}`} style={{ fontFamily: 'Fredoka' }}>
                  {a.count}
                </span>
                <span className="text-xs text-[#6B6B7A]">issue{a.count === 1 ? '' : 's'}</span>
              </div>
              {a.amount_at_risk > 0 && (
                <div className="text-[11px] text-[#6B6B7A] mt-1">{fmtMoney(a.amount_at_risk)} at risk</div>
              )}
            </button>
          ))}
        </div>

        {data && (data.audits || []).some((a) => a.count > 0) && (
          <div className="mt-4 flex justify-end">
            <Button onClick={() => setOpen(true)} size="sm" className="rounded-full bg-[#1E1E24] hover:bg-[#1E1E24]/90 text-white"
              data-testid="recon-view-details">
              View details <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="recon-details-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka' }}>Reconciliation Details</DialogTitle>
            <DialogDescription>
              Score {score} · {palette.label}. Each audit below shows top-10 examples — re-run to refresh.
            </DialogDescription>
          </DialogHeader>

          {(data?.audits || []).map((a) => (
            <div key={a.name} className="mb-5 border border-[#E8E5DF] rounded-xl overflow-hidden">
              <div className="flex items-center justify-between p-3 bg-[#FFFDF8] border-b border-[#E8E5DF]">
                <div>
                  <p className="text-sm font-semibold text-[#1E1E24]">{a.name}</p>
                  <p className="text-xs text-[#6B6B7A]">{a.count} issue{a.count === 1 ? '' : 's'}{a.amount_at_risk > 0 ? ` · ${fmtMoney(a.amount_at_risk)} at risk` : ''}</p>
                </div>
                <div className="flex items-center gap-2">
                  {a.fix_action === 'remirror_stripe' && a.count > 0 && (
                    <Button size="sm" disabled={busy} onClick={runRemirror}
                      className="rounded-full bg-[#FF3B6B] hover:bg-[#FF3B6B]/90 text-white" data-testid="recon-fix-remirror">
                      Re-mirror missing
                    </Button>
                  )}
                  {a.fix_action === 'recompute_balance' && a.count > 0 && (
                    <Button size="sm" disabled={busy} onClick={runRecomputeAll}
                      className="rounded-full bg-[#FF3B6B] hover:bg-[#FF3B6B]/90 text-white" data-testid="recon-fix-recompute-all">
                      Recompute all
                    </Button>
                  )}
                </div>
              </div>
              {a.count === 0 ? (
                <div className="p-6 text-center text-sm text-[#0FAB66] flex items-center justify-center gap-2">
                  <CheckCircle2 className="w-5 h-5" /> No issues found.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-[#FAF7F0] border-b border-[#E8E5DF]">
                      <tr>
                        {a.fix_action === 'remirror_stripe' && (<>
                          <th className="text-left p-2 font-semibold">Kind</th>
                          <th className="text-left p-2 font-semibold">Booking</th>
                          <th className="text-left p-2 font-semibold">Session/Txn</th>
                          <th className="text-left p-2 font-semibold">Method</th>
                          <th className="text-right p-2 font-semibold">Amount</th>
                          <th className="text-left p-2 font-semibold">Date</th>
                        </>)}
                        {a.fix_action === 'recompute_balance' && (<>
                          <th className="text-left p-2 font-semibold">Booking</th>
                          <th className="text-left p-2 font-semibold">Customer</th>
                          <th className="text-left p-2 font-semibold">Date</th>
                          <th className="text-right p-2 font-semibold">Total</th>
                          <th className="text-right p-2 font-semibold">Paid</th>
                          <th className="text-right p-2 font-semibold">Stored</th>
                          <th className="text-right p-2 font-semibold">Expected</th>
                          <th className="text-right p-2 font-semibold">Drift</th>
                          <th className="text-center p-2 font-semibold">Action</th>
                        </>)}
                        {!a.fix_action && (<>
                          <th className="text-left p-2 font-semibold">Booking</th>
                          <th className="text-left p-2 font-semibold">Customer</th>
                          <th className="text-left p-2 font-semibold">Date</th>
                          <th className="text-right p-2 font-semibold">Total</th>
                          <th className="text-right p-2 font-semibold">Expected HST</th>
                          <th className="text-right p-2 font-semibold">Collected HST</th>
                          <th className="text-right p-2 font-semibold">Drift</th>
                        </>)}
                      </tr>
                    </thead>
                    <tbody data-testid={`recon-audit-rows-${a.fix_action || 'hst'}`}>
                      {a.examples.map((ex, i) => (
                        <tr key={i} className="border-b border-[#F5F1E8]">
                          {a.fix_action === 'remirror_stripe' && (<>
                            <td className="p-2">{ex.kind === 'pt_missing_in_ledger' ? 'Missing in ledger' : 'Missing in PT'}</td>
                            <td className="p-2 font-mono text-[10px] text-[#6B6B7A]">{(ex.booking_id || '').replace('booking_', '') || '—'}</td>
                            <td className="p-2 font-mono text-[10px] text-[#6B6B7A] truncate max-w-[160px]">{ex.session_id || ex.gateway_transaction_id || ex.transaction_id || '—'}</td>
                            <td className="p-2 capitalize">{ex.payment_method || '—'}</td>
                            <td className="p-2 text-right font-semibold">{fmtMoney(ex.amount)}</td>
                            <td className="p-2 text-[#6B6B7A]">{(ex.created_at || '').slice(0, 10)}</td>
                          </>)}
                          {a.fix_action === 'recompute_balance' && (<>
                            <td className="p-2 font-mono text-[10px] text-[#6B6B7A]">{(ex.booking_id || '').replace('booking_', '')}</td>
                            <td className="p-2">{ex.customer_name} <span className="text-[#6B6B7A]">· {ex.child_name}</span></td>
                            <td className="p-2 text-[#6B6B7A]">{ex.date}</td>
                            <td className="p-2 text-right">{fmtMoney(ex.total_amount)}</td>
                            <td className="p-2 text-right">{fmtMoney(ex.paid_in_ledger)}</td>
                            <td className="p-2 text-right">{fmtMoney(ex.stored_balance)}</td>
                            <td className="p-2 text-right">{fmtMoney(ex.expected_balance)}</td>
                            <td className={`p-2 text-right font-semibold ${ex.drift > 0 ? 'text-[#D4302A]' : 'text-[#FF7A2E]'}`}>{fmtMoneySign(ex.drift)}</td>
                            <td className="p-2 text-center">
                              <Button size="sm" variant="outline" disabled={busy}
                                onClick={() => runRecomputeOne(ex.booking_id)}
                                className="rounded-full h-7 text-[11px] px-3 border-[#E8E5DF]"
                                data-testid={`recon-recompute-${ex.booking_id}`}>
                                Recompute
                              </Button>
                            </td>
                          </>)}
                          {!a.fix_action && (<>
                            <td className="p-2 font-mono text-[10px] text-[#6B6B7A]">{(ex.booking_id || '').replace('booking_', '')}</td>
                            <td className="p-2">{ex.customer_name} <span className="text-[#6B6B7A]">· {ex.child_name}</span></td>
                            <td className="p-2 text-[#6B6B7A]">{ex.date}</td>
                            <td className="p-2 text-right">{fmtMoney(ex.total_amount)}</td>
                            <td className="p-2 text-right">{fmtMoney(ex.expected_hst)}</td>
                            <td className="p-2 text-right">{fmtMoney(ex.collected_hst)}</td>
                            <td className={`p-2 text-right font-semibold ${ex.drift > 0 ? 'text-[#D4302A]' : 'text-[#FF7A2E]'}`}>{fmtMoneySign(ex.drift)}</td>
                          </>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {a.count > a.examples.length && (
                    <div className="p-2 text-[11px] text-[#6B6B7A] text-center bg-[#FAF7F0]">
                      Showing top {a.examples.length} of {a.count}. Run the bulk action to address all.
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ────────  Dashboard Tab  ────────
const DashboardTab = ({ period, customStart, customEnd, setPeriod, setCustomStart, setCustomEnd }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);   // { bucket, category?, source?, label }
  const [showDiscounts, setShowDiscounts] = useState(false);
  const [showPayroll, setShowPayroll] = useState(false);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === 'custom') { params.start = customStart; params.end = customEnd; }
      const res = await axios.get(`${API_URL}/api/admin/accounting/dashboard`, { params, withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [period, customStart, customEnd]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const openDrill = (bucket, label, extra = {}) => setDrill({ bucket, label, ...extra });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PeriodPicker
          period={period} setPeriod={setPeriod}
          customStart={customStart} setCustomStart={setCustomStart}
          customEnd={customEnd} setCustomEnd={setCustomEnd}
          label={data?.period?.label}
        />
        <Button
          onClick={fetchDashboard} disabled={loading} variant="outline" size="sm"
          className="rounded-full text-xs border-[#E8E5DF]"
          data-testid="acct-dashboard-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Top metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <SummaryCard
          icon={TrendingUp} label="Total Revenue"
          value={fmtMoney(data?.revenue?.gross)}
          sub={`${fmtMoney(data?.revenue?.pre_tax)} pre-tax + ${fmtMoney(data?.revenue?.hst)} HST`}
          color="#0FAB66" testId="acct-card-revenue"
          onDrillDown={() => openDrill('revenue', 'Total Revenue')}
        />
        <SummaryCard
          icon={TrendingDown} label="Total Expenses"
          value={fmtMoney(data?.expenses?.pre_tax)}
          sub={`COGS ${fmtMoney(data?.expenses?.cogs)} · Op ${fmtMoney(data?.expenses?.operating)}`}
          color="#D4302A" testId="acct-card-expenses"
          onDrillDown={() => openDrill('expense', 'Total Expenses')}
        />
        <SummaryCard
          icon={Wallet}
          label={Number(data?.net_profit) >= 0 ? "Net Profit" : "Net Loss"}
          value={fmtMoneySign(data?.net_profit)}
          sub={`Gross profit ${fmtMoney(data?.gross_profit)}`}
          color={Number(data?.net_profit) >= 0 ? '#0FAB66' : '#D4302A'}
          testId="acct-card-net-profit"
          onDrillDown={() => openDrill('net_profit', Number(data?.net_profit) >= 0 ? 'Net Profit' : 'Net Loss')}
        />
        <SummaryCard
          icon={Calculator}
          label={data?.hst?.is_refundable ? "HST Refundable" : "HST Payable"}
          value={fmtMoney(Math.abs(data?.hst?.net_payable || 0))}
          sub={`Collected ${fmtMoney(data?.hst?.collected)} − Paid ${fmtMoney(data?.hst?.paid)}`}
          color={data?.hst?.is_refundable ? '#0FAB66' : '#FF7A2E'}
          testId="acct-card-hst"
          onDrillDown={() => openDrill('hst', 'HST Detail')}
        />
        <SummaryCard
          icon={Receipt}
          label="Outstanding A/R"
          value={fmtMoney(data?.accounts_receivable?.total)}
          sub={`${data?.accounts_receivable?.count || 0} bookings unpaid`}
          color="#9333EA" testId="acct-card-ar"
          onDrillDown={() => openDrill('ar', 'Outstanding A/R')}
        />
      </div>

      {/* P&L overview */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5">
        <h3 className="text-lg font-bold text-[#1E1E24] mb-3" style={{ fontFamily: 'Fredoka' }}>
          P&L Overview — {data?.period?.label || 'Loading…'}
        </h3>
        {loading ? (
          <div className="py-12 flex justify-center"><RefreshCw className="w-6 h-6 animate-spin text-[#FF3B6B]" /></div>
        ) : (
          <div className="space-y-1">
            <PnlRow label="Booking Revenue" value={data?.revenue?.booking_revenue}
              onDrillDown={() => openDrill('revenue', 'Booking Revenue', { source: 'bookings' })}
              testId="acct-pnl-booking-revenue" />
            <PnlRow label="Manual Revenue" value={data?.revenue?.manual_revenue}
              onDrillDown={() => openDrill('revenue', 'Manual Revenue', { source: 'manual' })}
              testId="acct-pnl-manual-revenue" />
            {Number(data?.revenue?.pos_revenue) > 0 && (
              <PnlRow label="POS / Walk-in Revenue" value={data?.revenue?.pos_revenue}
                onDrillDown={() => openDrill('revenue', 'POS / Walk-in', { source: 'pos' })}
                testId="acct-pnl-pos-revenue" />
            )}
            {Number(data?.revenue?.discounts) > 0 && (
              <>
                <PnlRow label="Gross Revenue" value={data?.revenue?.gross_revenue ?? data?.revenue?.pre_tax} bold />
                <PnlRow label="Less: Discounts Given" value={-data?.revenue?.discounts} expense
                  testId="acct-dash-discounts"
                  onDrillDown={() => setShowDiscounts(true)} />
              </>
            )}
            {Number(data?.revenue?.refunds) > 0 && (
              <PnlRow label="Less: Refunds Issued" value={-data?.revenue?.refunds} expense
                testId="acct-pnl-refunds" />
            )}
            <PnlRow label="Net Revenue" value={data?.revenue?.pre_tax} bold totalLine
              testId="acct-pnl-net-revenue"
              onDrillDown={() => openDrill('revenue', 'Net Revenue')} />
            <div className="h-2" />
            <PnlRow label="Cost of Goods Sold" value={-data?.expenses?.cogs} expense
              onDrillDown={() => openDrill('expense', 'Cost of Goods Sold')} />
            <PnlRow label="Gross Profit" value={data?.gross_profit} bold accent={data?.gross_profit >= 0 ? '#0FAB66' : '#D4302A'} />
            <div className="h-2" />
            {Number(data?.expenses?.payroll) > 0 && (
              <PnlRow label="Payroll" value={-data?.expenses?.payroll} expense testId="acct-dash-payroll"
                onDrillDown={() => setShowPayroll(true)} />
            )}
            <PnlRow label="Operating Expenses (Total)" value={-data?.expenses?.operating} expense
              onDrillDown={() => openDrill('expense', 'Operating Expenses')} />
            <PnlRow
              label={data?.net_profit >= 0 ? "Net Profit" : "Net Loss"}
              value={data?.net_profit} bold totalLine
              testId="acct-pnl-net-profit"
              accent={data?.net_profit >= 0 ? '#0FAB66' : '#D4302A'}
            />
          </div>
        )}
      </div>

      {/* iter-133 — Reconciliation Health */}
      <ReconciliationHealthCard />

      <AccountingDefinitionsCard />

      <DrillDownDialog
        open={!!drill}
        onOpenChange={(o) => !o && setDrill(null)}
        drill={drill}
        period={period}
        customStart={customStart}
        customEnd={customEnd}
      />
      <DiscountsDrillDownDialog
        open={showDiscounts}
        onOpenChange={setShowDiscounts}
        period={period}
        customStart={customStart}
        customEnd={customEnd}
      />
      <PayrollDrillDownDialog
        open={showPayroll}
        onOpenChange={setShowPayroll}
        period={period}
        customStart={customStart}
        customEnd={customEnd}
      />
    </div>
  );
};

const PnlRow = ({ label, value, bold, expense, accent, totalLine, onDrillDown, testId }) => {
  const clickable = typeof onDrillDown === 'function';
  const Tag = clickable ? 'button' : 'div';
  return (
    <Tag
      type={clickable ? 'button' : undefined}
      onClick={clickable ? onDrillDown : undefined}
      data-testid={testId}
      className={`flex items-center justify-between py-1.5 w-full text-left ${totalLine ? 'border-t border-[#E8E5DF] pt-2 mt-1' : ''} ${clickable ? 'hover:bg-[#FFFDF8] rounded-md px-2 -mx-2 cursor-pointer' : ''}`}
    >
      <span className={`text-sm flex items-center gap-1 ${bold ? 'font-bold' : 'text-[#6B6B7A]'}`} style={{ color: accent || (bold ? '#1E1E24' : '#6B6B7A') }}>
        {label}
        {clickable && <ChevronRight className="w-3 h-3 opacity-30" />}
      </span>
      <span className={`text-sm ${bold ? 'font-bold' : 'tabular-nums'}`} style={{ color: accent || (expense ? '#D4302A' : '#1E1E24') }}>
        {fmtMoneySign(value)}
      </span>
    </Tag>
  );
};

// ────────  Accounting Definitions (in-CRM user guide)  ────────
// Documents the revenue policy so staff read the same rules the code enforces:
// revenue = money actually received; no payment → no revenue.
const ACCT_DEFINITIONS = [
  ['Gross Revenue', 'Sum of all completed payments received (all methods — cash, e-transfer, card, PayPal, gift card, cheque, debit).'],
  ['Refunds', 'Sum of all completed refunds issued to customers.'],
  ['Net Revenue', 'Gross Revenue − Refunds. This is what flows into the P&L and profit.'],
  ['Outstanding A/R', 'Total unpaid balances on confirmed / partially-paid / past-due bookings only.'],
  ['Cancelled / No-Show Revenue', '$0 — never counted as revenue. A kept deposit stays revenue until it is refunded.'],
  ['Deposit Received', 'Counted as revenue on the date it was received (not the party date).'],
  ['Balance Received', 'Counted as revenue on the date it was received.'],
];

const AccountingDefinitionsCard = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5" data-testid="acct-definitions-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-between w-full text-left"
        data-testid="acct-definitions-toggle"
      >
        <span className="flex items-center gap-2 text-lg font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>
          <BookOpen className="w-5 h-5 text-[#FF3B6B]" />
          Accounting Definitions
        </span>
        <ChevronRight className={`w-4 h-4 text-[#6B6B7A] transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      <p className="text-xs text-[#6B6B7A] mt-1">
        The rule is simple: <strong className="text-[#1E1E24]">revenue equals money actually received</strong>. No payment → no revenue.
      </p>
      {open && (
        <div className="mt-4 divide-y divide-[#F0EEE9]" data-testid="acct-definitions-list">
          {ACCT_DEFINITIONS.map(([term, def]) => (
            <div key={term} className="py-2.5 grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-1 sm:gap-4">
              <span className="text-sm font-semibold text-[#1E1E24]">{term}</span>
              <span className="text-sm text-[#6B6B7A]">{def}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ────────  Drill-Down Modal  ────────
const PAGE_SIZE = 25;
const SOURCE_LABEL = {
  bookings: 'Booking',
  manual: 'Manual',
  bank_income: 'Bank',
  bank_expenses: 'Bank',
  expenses: 'Expense',
  bank_imports: 'Bank',
};

const DrillDownDialog = ({ open, onOpenChange, drill, period, customStart, customEnd }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!open || !drill) { setData(null); setPage(0); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = {
          bucket: drill.bucket, period,
          limit: PAGE_SIZE, offset: page * PAGE_SIZE,
        };
        if (drill.category) params.category = drill.category;
        if (drill.source) params.source = drill.source;
        if (period === 'custom') { params.start = customStart; params.end = customEnd; }
        const r = await axios.get(`${API_URL}/api/admin/accounting/transactions`, { params, withCredentials: true });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || 'Failed to load transactions');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, drill, period, customStart, customEnd, page]);

  const handleExportCsv = async () => {
    if (!drill) return;
    try {
      const params = { bucket: drill.bucket, period, export_format: 'csv' };
      if (drill.category) params.category = drill.category;
      if (drill.source) params.source = drill.source;
      if (period === 'custom') { params.start = customStart; params.end = customEnd; }
      const res = await axios.get(`${API_URL}/api/admin/accounting/transactions`, {
        params, withCredentials: true, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactions_${(drill.label || drill.bucket).replace(/\s+/g, '_')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) {
      toast.error('CSV export failed');
    }
  };

  if (!drill) return null;
  const total = data?.total_count || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="acct-drilldown-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#FF3B6B]" />
            {drill.label}
            {drill.category && drill.category !== drill.label && <Badge variant="outline" className="text-xs">{drill.category}</Badge>}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {data?.period?.label ? `${data.period.label} · ${data.period.start} → ${data.period.end}` : 'Loading…'}
          </DialogDescription>
        </DialogHeader>

        {/* Summary strip */}
        <div className="grid grid-cols-4 gap-3 mb-3 px-1">
          <div className="bg-[#FFFDF8] rounded-xl border border-[#E8E5DF] p-3">
            <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A]">Transactions</p>
            <p className="text-xl font-bold" data-testid="acct-drilldown-count">{total}</p>
          </div>
          <div className="bg-[#FFFDF8] rounded-xl border border-[#E8E5DF] p-3">
            <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A]">Pre-tax</p>
            <p className="text-xl font-bold" data-testid="acct-drilldown-total">{fmtMoneySign(data?.total_amount)}</p>
          </div>
          <div className="bg-[#FFFDF8] rounded-xl border border-[#E8E5DF] p-3">
            <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A]">HST</p>
            <p className="text-xl font-bold">{fmtMoney(data?.total_hst)}</p>
          </div>
          <div className="bg-[#FFFDF8] rounded-xl border border-[#E8E5DF] p-3">
            <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A]">Gross</p>
            <p className="text-xl font-bold" data-testid="acct-drilldown-gross">{fmtMoneySign(data?.total_gross)}</p>
          </div>
        </div>

        {/* Transactions table */}
        <div className="flex-1 overflow-y-auto border border-[#E8E5DF] rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-[#FFFDF8] sticky top-0">
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B6B7A]">
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Description</th>
                <th className="px-3 py-2">Party</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2 text-right">HST</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-10 text-[#6B6B7A]">
                  <RefreshCw className="w-5 h-5 animate-spin inline mr-2" /> Loading…
                </td></tr>
              ) : (data?.items || []).length === 0 ? (
                <tr><td colSpan={8} className="text-center py-10 text-[#6B6B7A]" data-testid="acct-drilldown-empty">
                  No transactions in this period.
                </td></tr>
              ) : (
                (data?.items || []).map((r) => (
                  <tr key={`${r.txn_id}-${r.source}`} className="border-t border-[#F0EDE6] hover:bg-[#FFFDF8]">
                    <td className="px-3 py-2 text-xs text-[#6B6B7A] whitespace-nowrap">{r.date}</td>
                    <td className="px-3 py-2 max-w-[280px] truncate" title={r.description}>{r.description}</td>
                    <td className="px-3 py-2 text-xs text-[#6B6B7A]">{r.party || '—'}</td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className="text-[10px]">{SOURCE_LABEL[r.source] || r.source}</Badge>
                    </td>
                    <td className="px-3 py-2 text-xs">{r.category || '—'}</td>
                    <td className="px-3 py-2 text-right text-xs tabular-nums">{r.hst > 0 ? fmtMoney(r.hst) : '—'}</td>
                    <td className={`px-3 py-2 text-right font-semibold tabular-nums ${r.amount < 0 ? 'text-[#D4302A]' : 'text-[#0FAB66]'}`}>
                      {fmtMoneySign(r.amount)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {r.link && (
                        <a
                          href={r.link}
                          target={r.link.startsWith('http') ? '_blank' : undefined}
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            if (!r.link.startsWith('http')) {
                              e.preventDefault();
                              onOpenChange(false);
                              window.history.pushState({}, '', r.link);
                              window.dispatchEvent(new PopStateEvent('popstate'));
                            }
                          }}
                          className="text-xs text-[#FF3B6B] hover:underline whitespace-nowrap"
                        >
                          {r.link_label || 'Open'} →
                        </a>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer — pagination + export */}
        <DialogFooter className="gap-2 pt-3 border-t border-[#E8E5DF]">
          {total > PAGE_SIZE && (
            <div className="flex items-center gap-2 mr-auto text-xs">
              <Button
                size="sm" variant="outline"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                data-testid="acct-drilldown-prev"
              >← Prev</Button>
              <span className="text-[#6B6B7A] tabular-nums">
                Page {page + 1} / {totalPages}
              </span>
              <Button
                size="sm" variant="outline"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                data-testid="acct-drilldown-next"
              >Next →</Button>
            </div>
          )}
          <Button
            variant="outline" onClick={handleExportCsv}
            className="rounded-full border-[#E8E5DF]"
            data-testid="acct-drilldown-export"
            disabled={total === 0}
          >
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
          <Button onClick={() => onOpenChange(false)} className="rounded-full bg-[#1E1E24] hover:bg-[#000] text-white" data-testid="acct-drilldown-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ──────── Discounts Drill-down Modal ────────
const DISCOUNT_KIND_COLOR = {
  promo: '#FF7A2E',
  special_offer: '#9333EA',
  loyalty_voucher: '#0FAB66',
  gift_card: '#2C81E5',
};

const DiscountsDrillDownDialog = ({ open, onOpenChange, period, customStart, customEnd }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterKind, setFilterKind] = useState(null);

  useEffect(() => {
    if (!open) { setData(null); setFilterKind(null); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = { period };
        if (period === 'custom') { params.start = customStart; params.end = customEnd; }
        const r = await axios.get(`${API_URL}/api/admin/accounting/discounts-detail`, { params, withCredentials: true });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || 'Failed to load discount detail');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, period, customStart, customEnd]);

  const rows = (data?.items || []).filter((r) => !filterKind || r.discount_kind === filterKind);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="acct-discounts-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowDownRight className="w-5 h-5 text-[#FF7A2E]" />
            Discounts Given
          </DialogTitle>
          <DialogDescription className="text-xs">
            {data?.period?.label ? `${data.period.label} · ${data.period.start} → ${data.period.end}` : 'Loading…'}
          </DialogDescription>
        </DialogHeader>

        {/* Per-kind summary chips (also click-to-filter) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 px-1">
          {[
            { kind: 'promo', label: 'Promo Codes' },
            { kind: 'special_offer', label: 'Special Offers' },
            { kind: 'loyalty_voucher', label: 'Loyalty Vouchers' },
            { kind: 'gift_card', label: 'Gift Cards' },
          ].map(({ kind, label }) => {
            const v = data?.totals_by_kind?.[kind] || 0;
            const active = filterKind === kind;
            return (
              <button
                key={kind}
                type="button"
                onClick={() => setFilterKind(active ? null : kind)}
                className={`text-left rounded-xl border p-3 transition-colors ${
                  active ? 'bg-[#FFEDF3] border-[#FF3B6B]' : 'bg-[#FFFDF8] border-[#E8E5DF] hover:bg-[#FFF7EE]'
                }`}
                data-testid={`acct-discounts-chip-${kind}`}
                style={{ borderLeftWidth: 4, borderLeftColor: DISCOUNT_KIND_COLOR[kind] }}
              >
                <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A]">{label}</p>
                <p className="text-lg font-bold tabular-nums">{fmtMoney(v)}</p>
              </button>
            );
          })}
        </div>

        {/* Total + filter clear */}
        <div className="flex items-center justify-between px-1 mb-2 text-xs">
          <span className="text-[#6B6B7A]">
            {filterKind ? (
              <button onClick={() => setFilterKind(null)} className="underline hover:text-[#FF3B6B]">
                Clear filter ({rows.length} of {data?.total_count || 0})
              </button>
            ) : (
              <>Total transactions: <span className="font-semibold text-[#1E1E24]">{data?.total_count || 0}</span></>
            )}
          </span>
          <span className="text-[#6B6B7A]">
            Total discounts: <span className="font-semibold text-[#D4302A] tabular-nums">{fmtMoney(data?.total_amount)}</span>
          </span>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto border border-[#E8E5DF] rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-[#FFFDF8] sticky top-0 z-10">
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B6B7A]">
                <th className="px-3 py-2">Date Applied</th>
                <th className="px-3 py-2">Customer</th>
                <th className="px-3 py-2">Child / Party</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2 text-right">HST</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-10 text-[#6B6B7A]">
                  <RefreshCw className="w-5 h-5 animate-spin inline mr-2" /> Loading…
                </td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-10 text-[#6B6B7A]" data-testid="acct-discounts-empty">
                  No discount transactions in this period.
                </td></tr>
              ) : (
                rows.map((r, i) => (
                  <tr key={`${r.booking_id}-${r.discount_kind}-${i}`} className="border-t border-[#F0EDE6] hover:bg-[#FFFDF8]">
                    <td className="px-3 py-2 text-xs text-[#6B6B7A] whitespace-nowrap">{r.date_applied || '—'}</td>
                    <td className="px-3 py-2">{r.customer_name}</td>
                    <td className="px-3 py-2 text-xs text-[#6B6B7A]">{r.child_name}</td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className="text-[10px]" style={{ borderColor: DISCOUNT_KIND_COLOR[r.discount_kind] }}>
                        {r.discount_type}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-xs font-mono">{r.discount_code || '—'}</td>
                    <td className="px-3 py-2 text-right text-xs tabular-nums">{r.amount_hst > 0 ? fmtMoney(r.amount_hst) : '—'}</td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums text-[#D4302A]">
                      -{fmtMoney(r.amount_pre_tax)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <a
                        href={r.link}
                        onClick={(e) => {
                          e.preventDefault();
                          onOpenChange(false);
                          window.history.pushState({}, '', r.link);
                          window.dispatchEvent(new PopStateEvent('popstate'));
                        }}
                        className="text-xs text-[#FF3B6B] hover:underline whitespace-nowrap"
                      >Open booking →</a>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <DialogFooter className="gap-2 pt-3 border-t border-[#E8E5DF]">
          <Button onClick={() => onOpenChange(false)} className="rounded-full bg-[#1E1E24] hover:bg-[#000] text-white" data-testid="acct-discounts-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ──────── Payroll Drill-down Modal ────────
const PayrollDrillDownDialog = ({ open, onOpenChange, period, customStart, customEnd }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!open) { setData(null); setSearch(''); setExpanded({}); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = { period };
        if (period === 'custom') { params.start = customStart; params.end = customEnd; }
        const r = await axios.get(`${API_URL}/api/admin/accounting/payroll-detail`, { params, withCredentials: true });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || 'Failed to load payroll detail');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, period, customStart, customEnd]);

  const downloadPaystubPdf = async (paystub_id) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/paystubs/${paystub_id}/pdf`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `paystub_${paystub_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to download paystub');
    }
  };

  const q = search.trim().toLowerCase();
  const employees = (data?.items || []).filter(
    (r) => !q || (r.employee_name || '').toLowerCase().includes(q) || (r.employee_email || '').toLowerCase().includes(q),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="acct-payroll-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#2C81E5]" />
            Payroll Detail
          </DialogTitle>
          <DialogDescription className="text-xs">
            {data?.period?.label ? `${data.period.label} · ${data.period.start} → ${data.period.end}` : 'Loading…'}
          </DialogDescription>
        </DialogHeader>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 px-1">
          <div className="bg-[#FFFDF8] rounded-xl border border-[#E8E5DF] p-3">
            <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A]">Employees</p>
            <p className="text-xl font-bold" data-testid="acct-payroll-employee-count">{data?.employee_count || 0}</p>
          </div>
          <div className="bg-[#FFFDF8] rounded-xl border border-[#E8E5DF] p-3">
            <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A]">Paystubs</p>
            <p className="text-xl font-bold">{data?.paystub_count || 0}</p>
          </div>
          <div className="bg-[#FFFDF8] rounded-xl border border-[#E8E5DF] p-3">
            <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A]">Gross Pay</p>
            <p className="text-xl font-bold tabular-nums" data-testid="acct-payroll-total-gross">{fmtMoney(data?.total_gross)}</p>
          </div>
          <div className="bg-[#FFFDF8] rounded-xl border border-[#E8E5DF] p-3">
            <p className="text-[10px] uppercase tracking-wider text-[#6B6B7A]">Net Pay</p>
            <p className="text-xl font-bold tabular-nums">{fmtMoney(data?.total_net)}</p>
          </div>
        </div>

        {/* Search */}
        <div className="px-1 mb-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B7A]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search employee by name or email…"
              className="pl-9 h-9 rounded-xl border-[#E8E5DF] text-sm"
              data-testid="acct-payroll-search"
            />
          </div>
        </div>

        {/* Employee list */}
        <div className="flex-1 overflow-y-auto border border-[#E8E5DF] rounded-xl">
          {loading ? (
            <div className="text-center py-10 text-[#6B6B7A]">
              <RefreshCw className="w-5 h-5 animate-spin inline mr-2" /> Loading…
            </div>
          ) : employees.length === 0 ? (
            <div className="text-center py-10 text-[#6B6B7A]" data-testid="acct-payroll-empty">
              {q ? 'No employees match your search.' : 'No paystubs in this period.'}
            </div>
          ) : (
            <ul className="divide-y divide-[#F0EDE6]">
              {employees.map((emp) => {
                const isOpen = !!expanded[emp.employee_id];
                return (
                  <li key={emp.employee_id} className="bg-white">
                    <button
                      type="button"
                      onClick={() => setExpanded((s) => ({ ...s, [emp.employee_id]: !s[emp.employee_id] }))}
                      className="w-full grid grid-cols-12 items-center gap-2 px-3 py-3 hover:bg-[#FFFDF8] text-left"
                      data-testid={`acct-payroll-emp-${emp.employee_id}`}
                    >
                      <div className="col-span-4">
                        <p className="font-semibold text-sm">{emp.employee_name}</p>
                        {emp.employee_email && <p className="text-[11px] text-[#6B6B7A]">{emp.employee_email}</p>}
                      </div>
                      <div className="col-span-2 text-xs">
                        <Badge variant="outline" className="text-[10px]">{emp.paystub_count} stub{emp.paystub_count === 1 ? '' : 's'}</Badge>
                      </div>
                      <div className="col-span-2 text-right tabular-nums text-sm">
                        <p className="text-[10px] uppercase text-[#6B6B7A]">Gross</p>
                        <p className="font-semibold">{fmtMoney(emp.gross_pay)}</p>
                      </div>
                      <div className="col-span-2 text-right tabular-nums text-sm">
                        <p className="text-[10px] uppercase text-[#6B6B7A]">Deductions</p>
                        <p className="text-[#D4302A]">-{fmtMoney(emp.deductions)}</p>
                      </div>
                      <div className="col-span-2 text-right tabular-nums text-sm">
                        <p className="text-[10px] uppercase text-[#6B6B7A]">Net</p>
                        <p className="font-semibold text-[#0FAB66]">{fmtMoney(emp.net_pay)}</p>
                      </div>
                    </button>

                    {isOpen && (
                      <div className="bg-[#FFFDF8] border-t border-[#F0EDE6]">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-left text-[10px] uppercase tracking-wider text-[#6B6B7A]">
                              <th className="px-3 py-2">Period</th>
                              <th className="px-3 py-2">Type</th>
                              <th className="px-3 py-2 text-right">Hours</th>
                              <th className="px-3 py-2 text-right">Gross</th>
                              <th className="px-3 py-2 text-right">Deduct.</th>
                              <th className="px-3 py-2 text-right">Net</th>
                              <th className="px-3 py-2"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {emp.paystubs.map((ps) => (
                              <tr key={ps.paystub_id} className="border-t border-[#F0EDE6]">
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {ps.pay_period_start} → {ps.pay_period_end}
                                </td>
                                <td className="px-3 py-2">
                                  <Badge variant="outline" className="text-[9px]">{ps.period_type}</Badge>
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">{ps.total_hours ?? '—'}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(ps.gross_pay)}</td>
                                <td className="px-3 py-2 text-right tabular-nums text-[#D4302A]">-{fmtMoney(ps.deduction_amount)}</td>
                                <td className="px-3 py-2 text-right tabular-nums font-semibold text-[#0FAB66]">{fmtMoney(ps.net_pay)}</td>
                                <td className="px-3 py-2 text-right">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="rounded-full text-[11px] h-7 px-2 border-[#E8E5DF]"
                                    onClick={(e) => { e.stopPropagation(); downloadPaystubPdf(ps.paystub_id); }}
                                    data-testid={`acct-payroll-pdf-${ps.paystub_id}`}
                                  >
                                    <FileDown className="w-3 h-3 mr-1" /> PDF
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter className="gap-2 pt-3 border-t border-[#E8E5DF]">
          <Button onClick={() => onOpenChange(false)} className="rounded-full bg-[#1E1E24] hover:bg-[#000] text-white" data-testid="acct-payroll-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ────────  P&L Tab  ────────
const PnlTab = ({ period, customStart, customEnd, setPeriod, setCustomStart, setCustomEnd }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);
  const [showDiscounts, setShowDiscounts] = useState(false);
  const [showPayroll, setShowPayroll] = useState(false);
  const openDrill = (bucket, label, extra = {}) => setDrill({ bucket, label, ...extra });

  const fetchPnl = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === 'custom') { params.start = customStart; params.end = customEnd; }
      const res = await axios.get(`${API_URL}/api/admin/accounting/pnl`, { params, withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load P&L');
    } finally {
      setLoading(false);
    }
  }, [period, customStart, customEnd]);

  useEffect(() => { fetchPnl(); }, [fetchPnl]);

  const handleExport = async (format) => {
    try {
      const params = { format, period };
      if (period === 'custom') { params.start = customStart; params.end = customEnd; }
      const res = await axios.get(`${API_URL}/api/admin/accounting/pnl/export`, {
        params, withCredentials: true, responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: format === 'pdf' ? 'application/pdf' : 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PnL_${data?.period?.label || 'report'}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`P&L exported as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PeriodPicker
          period={period} setPeriod={setPeriod}
          customStart={customStart} setCustomStart={setCustomStart}
          customEnd={customEnd} setCustomEnd={setCustomEnd}
          label={data?.period?.label}
        />
        <div className="flex items-center gap-2">
          <Button
            onClick={() => handleExport('csv')} disabled={loading} variant="outline" size="sm"
            className="rounded-full text-xs border-[#E8E5DF]"
            data-testid="acct-pnl-export-csv"
          >
            <Download className="w-3.5 h-3.5 mr-1" /> CSV
          </Button>
          <Button
            onClick={() => handleExport('pdf')} disabled={loading} size="sm"
            className="rounded-full text-xs bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
            data-testid="acct-pnl-export-pdf"
          >
            <FileDown className="w-3.5 h-3.5 mr-1" /> PDF
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-6 shadow-sm">
        <div className="text-center mb-5">
          <h2 className="text-2xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>
            Profit & Loss Statement
          </h2>
          <p className="text-sm text-[#6B6B7A]">
            {data?.period?.label} · {data?.period?.start} → {data?.period?.end}
          </p>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center"><RefreshCw className="w-6 h-6 animate-spin text-[#FF3B6B]" /></div>
        ) : (
          <div className="max-w-2xl mx-auto space-y-1">
            {/* Revenue */}
            <SectionHeader title="Revenue" color="#0FAB66" />
            <PnlRow label="Booking Revenue" value={data?.revenue?.booking_revenue}
              onDrillDown={() => openDrill('revenue', 'Booking Revenue', { source: 'bookings' })} />
            <PnlRow label="Manual Revenue (Other Income)" value={data?.revenue?.manual_revenue}
              onDrillDown={() => openDrill('revenue', 'Manual Revenue', { source: 'manual' })} />
            {Number(data?.revenue?.pos_revenue) > 0 && (
              <PnlRow label="POS / Walk-in Revenue" value={data?.revenue?.pos_revenue}
                onDrillDown={() => openDrill('revenue', 'POS / Walk-in', { source: 'pos' })} />
            )}
            {Number(data?.revenue?.bank_income) > 0 && (
              <PnlRow label="Bank Income (classified)" value={data?.revenue?.bank_income}
                onDrillDown={() => openDrill('revenue', 'Bank Income', { source: 'bank_income' })} />
            )}
            <PnlRow label="Gross Revenue (pre-tax)"
              value={data?.revenue?.gross_revenue ?? data?.revenue?.total}
              bold
              testId="acct-pnl-gross-revenue" />

            {/* Discounts (only show if any allocated) */}
            {Number(data?.revenue?.discounts) > 0 && (
              <>
                <div className="h-2" />
                <SectionHeader title="Less: Discounts Given" color="#FF7A2E" />
                {Object.entries(data?.revenue?.discounts_breakdown || {}).map(([key, v]) => {
                  if (!v || Number(v) <= 0) return null;
                  const labels = {
                    promo: 'Promo Codes',
                    special_offer: 'Special Offers',
                    loyalty_voucher: 'Loyalty Vouchers',
                    gift_card: 'Gift Cards',
                  };
                  return (
                    <PnlRow
                      key={key}
                      label={labels[key] || key}
                      value={-Number(v)}
                      expense
                      testId={`acct-pnl-discount-${key}`}
                      onDrillDown={() => setShowDiscounts(true)}
                    />
                  );
                })}
                <PnlRow label="Total Discounts" value={-data?.revenue?.discounts} bold totalLine expense
                  testId="acct-pnl-total-discounts"
                  onDrillDown={() => setShowDiscounts(true)} />
              </>
            )}

            <div className="h-2" />
            <PnlRow label="Net Revenue (pre-tax)"
              value={data?.revenue?.net_revenue ?? data?.revenue?.total}
              bold totalLine
              testId="acct-pnl-net-revenue"
              onDrillDown={() => openDrill('revenue', 'Net Revenue')} />
            <div className="text-[11px] text-[#6B6B7A] text-right">
              HST Collected: {fmtMoney(data?.revenue?.hst_collected)} · Gross w/ tax: {fmtMoney(data?.revenue?.gross_with_tax)}
            </div>

            {/* COGS */}
            <div className="h-3" />
            <SectionHeader title="Cost of Goods Sold (COGS)" color="#FFA02C" />
            {data?.cogs?.breakdown?.length > 0 ? (
              data.cogs.breakdown.map((c) => (
                <PnlRow key={c.category} label={`${c.category} (${c.count})`} value={-c.amount} expense
                  testId={`acct-pnl-cogs-${c.category.toLowerCase().replace(/\s+/g, '-')}`}
                  onDrillDown={() => openDrill('expense', c.category, { category: c.category })} />
              ))
            ) : (
              <div className="text-xs text-[#6B6B7A] italic py-1">No COGS recorded in this period.</div>
            )}
            <PnlRow label="Total COGS" value={-data?.cogs?.total} bold totalLine expense
              onDrillDown={() => openDrill('expense', 'Total COGS')} />

            <div className="h-3" />
            <PnlRow
              label="GROSS PROFIT"
              value={data?.gross_profit}
              bold
              accent={data?.gross_profit >= 0 ? '#0FAB66' : '#D4302A'}
            />
            <div className="text-[11px] text-[#6B6B7A] text-right">
              Margin: {data?.gross_margin_pct}%
            </div>

            {/* Operating Expenses */}
            <div className="h-3" />
            <SectionHeader title="Operating Expenses" color="#2C81E5" />
            {data?.operating_expenses?.breakdown?.length > 0 ? (
              data.operating_expenses.breakdown.map((c) => (
                <PnlRow
                  key={c.category}
                  label={c.is_payroll
                    ? `${c.category} — ${c.count} paystubs`
                    : `${c.category} (${c.count})`}
                  value={-c.amount}
                  expense
                  testId={c.is_payroll
                    ? 'acct-pnl-op-payroll'
                    : `acct-pnl-op-${c.category.toLowerCase().replace(/\s+/g, '-')}`}
                  onDrillDown={c.is_payroll
                    ? () => setShowPayroll(true)
                    : () => openDrill('expense', c.category, { category: c.category })} />
              ))
            ) : (
              <div className="text-xs text-[#6B6B7A] italic py-1">No operating expenses in this period.</div>
            )}
            <PnlRow label="Total Operating Expenses" value={-data?.operating_expenses?.total} bold totalLine expense
              onDrillDown={() => openDrill('expense', 'Total Operating Expenses')} />

            {/* Net Profit */}
            <div className="h-4" />
            <div
              className="rounded-xl px-4 py-3 flex items-center justify-between"
              style={{ backgroundColor: data?.net_profit >= 0 ? '#E5F8EE' : '#FFE8E5' }}
              data-testid="acct-pnl-net-profit"
            >
              <span className="text-base font-bold" style={{ color: data?.net_profit >= 0 ? '#0FAB66' : '#D4302A' }}>
                {data?.net_profit >= 0 ? 'NET PROFIT' : 'NET LOSS'}
              </span>
              <span className="text-xl font-bold" style={{ color: data?.net_profit >= 0 ? '#0FAB66' : '#D4302A' }}>
                {fmtMoneySign(data?.net_profit)}
              </span>
            </div>
            <div className="text-[11px] text-[#6B6B7A] text-right">
              Net margin: {data?.net_margin_pct}%
            </div>
          </div>
        )}
      </div>

      <DrillDownDialog
        open={!!drill}
        onOpenChange={(o) => !o && setDrill(null)}
        drill={drill}
        period={period}
        customStart={customStart}
        customEnd={customEnd}
      />
      <DiscountsDrillDownDialog
        open={showDiscounts}
        onOpenChange={setShowDiscounts}
        period={period}
        customStart={customStart}
        customEnd={customEnd}
      />
      <PayrollDrillDownDialog
        open={showPayroll}
        onOpenChange={setShowPayroll}
        period={period}
        customStart={customStart}
        customEnd={customEnd}
      />
    </div>
  );
};

const SectionHeader = ({ title, color }) => (
  <div className="text-[11px] uppercase tracking-wider font-bold pt-2 pb-1" style={{ color }}>
    {title}
  </div>
);

// ────────  Balance Sheet Tab  ────────
const BalanceSheetTab = () => {
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchBs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/accounting/balance-sheet`, {
        params: { as_of: asOf }, withCredentials: true,
      });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load Balance Sheet');
    } finally {
      setLoading(false);
    }
  }, [asOf]);

  useEffect(() => { fetchBs(); }, [fetchBs]);

  const handleExport = async (format) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/accounting/balance-sheet/export`, {
        params: { format, as_of: asOf }, withCredentials: true, responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: format === 'pdf' ? 'application/pdf' : 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `BalanceSheet_${asOf}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Balance Sheet exported as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#6B6B7A]" />
          <label className="text-sm text-[#6B6B7A]">As of:</label>
          <Input
            type="date"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
            className="h-9 rounded-xl border-[#E8E5DF] text-sm w-40"
            data-testid="acct-bs-asof"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => handleExport('csv')} variant="outline" size="sm" className="rounded-full text-xs border-[#E8E5DF]" data-testid="acct-bs-export-csv">
            <Download className="w-3.5 h-3.5 mr-1" /> CSV
          </Button>
          <Button onClick={() => handleExport('pdf')} size="sm" className="rounded-full text-xs bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="acct-bs-export-pdf">
            <FileDown className="w-3.5 h-3.5 mr-1" /> PDF
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-6 shadow-sm">
        <div className="text-center mb-5">
          <h2 className="text-2xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Balance Sheet</h2>
          <p className="text-sm text-[#6B6B7A]">As of {data?.as_of}</p>
          {data?.balanced && (
            <Badge variant="outline" className="mt-2 border-green-300 text-green-700 bg-green-50">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Balanced
            </Badge>
          )}
          {data && !data.balanced && (
            <Badge variant="outline" className="mt-2 border-red-300 text-red-700 bg-red-50">
              <AlertCircle className="w-3 h-3 mr-1" /> Out of balance — review entries
            </Badge>
          )}
        </div>

        {loading ? (
          <div className="py-16 flex justify-center"><RefreshCw className="w-6 h-6 animate-spin text-[#FF3B6B]" /></div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Assets */}
            <div className="bg-green-50 border border-green-200 rounded-2xl p-5">
              <h3 className="text-base font-bold text-green-800 mb-3 flex items-center" style={{ fontFamily: 'Fredoka' }}>
                <ArrowUpRight className="w-4 h-4 mr-1" /> Assets
              </h3>
              <div className="space-y-2">
                <BsRow label="Cash" value={data?.assets?.cash} />
                <BsRow label={`A/R (${data?.assets?.accounts_receivable_count || 0} bookings)`} value={data?.assets?.accounts_receivable} />
                <BsRow label={`Inventory (${data?.assets?.inventory_item_count || 0} items)`} value={data?.assets?.inventory} />
                {data?.assets?.hst_refundable > 0 && (
                  <BsRow label="HST Refundable" value={data?.assets?.hst_refundable} />
                )}
                <BsRow label="Total Assets" value={data?.assets?.total} bold />
              </div>
            </div>

            {/* Liabilities + Equity */}
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
                <h3 className="text-base font-bold text-red-800 mb-3 flex items-center" style={{ fontFamily: 'Fredoka' }}>
                  <ArrowDownRight className="w-4 h-4 mr-1" /> Liabilities
                </h3>
                <div className="space-y-2">
                  <BsRow label="HST Payable" value={data?.liabilities?.hst_payable} />
                  <BsRow label={`A/P (${data?.liabilities?.accounts_payable_count || 0} unpaid)`} value={data?.liabilities?.accounts_payable} />
                  <BsRow label="Total Liabilities" value={data?.liabilities?.total} bold />
                </div>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-2xl p-5">
                <h3 className="text-base font-bold text-purple-800 mb-3 flex items-center" style={{ fontFamily: 'Fredoka' }}>
                  <Wallet className="w-4 h-4 mr-1" /> Equity
                </h3>
                <div className="space-y-2">
                  <BsRow label="Opening Equity" value={data?.equity?.opening_equity} />
                  <BsRow label="Retained Earnings" value={data?.equity?.retained_earnings} />
                  <BsRow label="Total Equity" value={data?.equity?.total} bold />
                </div>
              </div>
              <div className="bg-[#FFFDF8] border border-[#E8E5DF] rounded-2xl p-3 text-center text-xs text-[#6B6B7A]">
                Liabilities + Equity = <strong>{fmtMoney(Number(data?.liabilities?.total || 0) + Number(data?.equity?.total || 0))}</strong>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const BsRow = ({ label, value, bold }) => (
  <div className={`flex items-center justify-between ${bold ? 'pt-2 mt-1 border-t border-current/20 font-bold' : ''}`}>
    <span className="text-sm">{label}</span>
    <span className="text-sm tabular-nums">{fmtMoneySign(value)}</span>
  </div>
);

// ────────  Expenses Tab  ────────
const ExpensesTab = ({ period, customStart, customEnd, setPeriod, setCustomStart, setCustomEnd }) => {
  const [expenses, setExpenses] = useState([]);
  const [byCategory, setByCategory] = useState([]);
  const [totals, setTotals] = useState({ amount: 0, tax: 0, grand_total: 0 });
  const [categories, setCategories] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [paid, setPaid] = useState('all'); // all|paid|unpaid
  const [editing, setEditing] = useState(null); // null | 'new' | expense

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === 'custom') { params.start = customStart; params.end = customEnd; }
      if (category !== 'all') params.category = category;
      if (paid !== 'all') params.paid = paid === 'paid';
      if (search.trim()) params.search = search.trim();
      const res = await axios.get(`${API_URL}/api/admin/accounting/expenses`, { params, withCredentials: true });
      setExpenses(res.data.rows || []);
      setByCategory(res.data.by_category || []);
      setTotals(res.data.totals || { amount: 0, tax: 0, grand_total: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [period, customStart, customEnd, category, paid, search]);

  const fetchCats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/accounting/categories`, { withCredentials: true });
      setCategories(res.data.expense_categories || []);
      setPaymentMethods(res.data.payment_methods || []);
    } catch (e) { /* non-critical */ }
  }, []);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);
  useEffect(() => { fetchCats(); }, [fetchCats]);

  const handleDelete = async (id, label) => {
    if (!window.confirm(`Delete expense "${label}"? This soft-deletes the record.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/accounting/expenses/${id}`, { withCredentials: true });
      toast.success('Expense deleted');
      fetchExpenses();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PeriodPicker
          period={period} setPeriod={setPeriod}
          customStart={customStart} setCustomStart={setCustomStart}
          customEnd={customEnd} setCustomEnd={setCustomEnd}
        />
        <Button
          onClick={() => setEditing('new')}
          size="sm"
          className="rounded-full text-xs bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
          data-testid="acct-expense-add"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Expense
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard icon={Receipt} label="Total Expenses" value={fmtMoney(totals.amount)} sub="pre-tax" color="#D4302A" testId="acct-exp-total" />
        <SummaryCard icon={Calculator} label="HST Paid" value={fmtMoney(totals.tax)} sub="recoverable input credit" color="#FF7A2E" testId="acct-exp-hst" />
        <SummaryCard icon={DollarSign} label="Grand Total" value={fmtMoney(totals.grand_total)} sub="incl. tax" color="#1B0B2E" testId="acct-exp-grand" />
        <SummaryCard icon={PieChart} label="Categories" value={byCategory.length} sub={`across ${expenses.length} entries`} color="#9333EA" testId="acct-exp-cats" />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-[#6B6B7A] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search description, vendor, notes…"
            className="h-9 pl-8 rounded-xl border-[#E8E5DF] text-sm w-64"
            data-testid="acct-exp-search"
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="h-9 rounded-xl border border-[#E8E5DF] text-sm bg-white px-3"
          data-testid="acct-exp-cat-filter"
        >
          <option value="all">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          value={paid}
          onChange={(e) => setPaid(e.target.value)}
          className="h-9 rounded-xl border border-[#E8E5DF] text-sm bg-white px-3"
          data-testid="acct-exp-paid-filter"
        >
          <option value="all">All (paid + unpaid)</option>
          <option value="paid">Paid only</option>
          <option value="unpaid">Unpaid (A/P)</option>
        </select>
        <Button
          onClick={fetchExpenses}
          variant="outline" size="sm"
          className="rounded-full text-xs border-[#E8E5DF] ml-auto"
          data-testid="acct-exp-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Expenses table */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center"><RefreshCw className="w-6 h-6 animate-spin text-[#FF3B6B]" /></div>
        ) : expenses.length === 0 ? (
          <div className="py-16 text-center">
            <Receipt className="w-10 h-10 text-[#E8E5DF] mx-auto mb-3" />
            <p className="text-sm text-[#6B6B7A] mb-3">No expenses in this period.</p>
            <Button
              onClick={() => setEditing('new')}
              size="sm"
              className="rounded-full text-xs bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add your first expense
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="acct-exp-table">
              <thead className="bg-[#FFFDF8] text-[11px] uppercase tracking-wider text-[#6B6B7A]">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Date</th>
                  <th className="text-left px-4 py-2 font-semibold">Description</th>
                  <th className="text-left px-4 py-2 font-semibold">Vendor</th>
                  <th className="text-left px-4 py-2 font-semibold">Category</th>
                  <th className="text-right px-4 py-2 font-semibold">Amount</th>
                  <th className="text-right px-4 py-2 font-semibold">HST</th>
                  <th className="text-right px-4 py-2 font-semibold">Total</th>
                  <th className="text-center px-4 py-2 font-semibold">Status</th>
                  <th className="text-right px-4 py-2 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0EDE6]">
                {expenses.map((e) => (
                  <tr key={e.expense_id} className="hover:bg-[#FFFDF8]" data-testid={`acct-exp-row-${e.expense_id}`}>
                    <td className="px-4 py-2 tabular-nums text-xs">{e.date}</td>
                    <td className="px-4 py-2">
                      <div className="font-medium text-[#1E1E24]">{e.description}</div>
                      {e.notes && <div className="text-[11px] text-[#6B6B7A] italic">{e.notes}</div>}
                    </td>
                    <td className="px-4 py-2 text-[#6B6B7A] text-xs">{e.vendor || '—'}</td>
                    <td className="px-4 py-2">
                      <Badge variant="outline" className="text-[10px]">{e.category}</Badge>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmtMoney(e.amount)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-[#6B6B7A]">{fmtMoney(e.tax_amount)}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-semibold">{fmtMoney(e.total)}</td>
                    <td className="px-4 py-2 text-center">
                      {e.paid ? (
                        <Badge variant="outline" className="text-[10px] border-green-300 text-green-700 bg-green-50">Paid</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] border-orange-300 text-orange-700 bg-orange-50">Unpaid (A/P)</Badge>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {e.receipt_url && (
                          <button
                            type="button"
                            onClick={() => window.open(e.receipt_url, '_blank')}
                            className="p-1.5 rounded-lg text-[#6B6B7A] hover:bg-[#FFFDF8] hover:text-[#FF3B6B]"
                            title="View receipt"
                            data-testid={`acct-exp-receipt-${e.expense_id}`}
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setEditing(e)}
                          className="p-1.5 rounded-lg text-[#6B6B7A] hover:bg-[#FFFDF8] hover:text-[#FF3B6B]"
                          title="Edit"
                          data-testid={`acct-exp-edit-${e.expense_id}`}
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(e.expense_id, e.description)}
                          className="p-1.5 rounded-lg text-[#6B6B7A] hover:bg-red-50 hover:text-red-600"
                          title="Delete"
                          data-testid={`acct-exp-delete-${e.expense_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Category breakdown */}
      {byCategory.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5">
          <h3 className="text-base font-bold text-[#1E1E24] mb-3" style={{ fontFamily: 'Fredoka' }}>By Category</h3>
          <div className="space-y-1">
            {byCategory.map((c) => (
              <div key={c.category} className="flex items-center justify-between py-1 text-sm">
                <span>
                  <span className="font-medium">{c.category}</span>
                  <span className="text-[#6B6B7A] text-xs ml-2">({c.count})</span>
                </span>
                <span className="tabular-nums">
                  {fmtMoney(c.amount)} <span className="text-[#6B6B7A] text-xs">+ {fmtMoney(c.tax)} HST</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {editing && (
        <ExpenseDialog
          mode={editing === 'new' ? 'new' : 'edit'}
          initial={editing === 'new' ? null : editing}
          categories={categories}
          paymentMethods={paymentMethods}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchExpenses(); }}
        />
      )}
    </div>
  );
};

// ────────  Expense Add/Edit Dialog  ────────
const ExpenseDialog = ({ mode, initial, categories, paymentMethods, onClose, onSaved }) => {
  const [form, setForm] = useState({
    date: initial?.date || new Date().toISOString().slice(0, 10),
    amount: initial?.amount ?? '',
    tax_amount: initial?.tax_amount ?? '',
    category: initial?.category || (categories[0] || 'Other'),
    vendor: initial?.vendor || '',
    description: initial?.description || '',
    payment_method: initial?.payment_method || 'cash',
    receipt_url: initial?.receipt_url || '',
    paid: initial?.paid ?? true,
    notes: initial?.notes || '',
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleReceiptUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API_URL}/api/admin/accounting/expenses/upload-receipt`, fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setForm((p) => ({ ...p, receipt_url: res.data.url }));
      toast.success('Receipt uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    if (!form.amount || Number(form.amount) < 0) { toast.error('Amount must be ≥ 0'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: Number(form.amount),
        tax_amount: form.tax_amount === '' ? null : Number(form.tax_amount),
      };
      if (mode === 'new') {
        await axios.post(`${API_URL}/api/admin/accounting/expenses`, payload, { withCredentials: true });
        toast.success('Expense created');
      } else {
        await axios.put(`${API_URL}/api/admin/accounting/expenses/${initial.expense_id}`, payload, { withCredentials: true });
        toast.success('Expense updated');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg" data-testid="acct-expense-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Fredoka' }}>
            {mode === 'new' ? 'Add Expense' : `Edit Expense`}
          </DialogTitle>
          <DialogDescription>Track a business expense with HST/GST calculated automatically.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-[#6B6B7A]">Date *</label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-9 rounded-xl border-[#E8E5DF] text-sm" data-testid="acct-exp-form-date" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B6B7A]">Category *</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="h-9 w-full rounded-xl border border-[#E8E5DF] text-sm bg-white px-3"
                data-testid="acct-exp-form-category"
              >
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#6B6B7A]">Description *</label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Printer ink cartridges" className="h-9 rounded-xl border-[#E8E5DF] text-sm" data-testid="acct-exp-form-desc" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#6B6B7A]">Vendor</label>
            <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="e.g. Staples" className="h-9 rounded-xl border-[#E8E5DF] text-sm" data-testid="acct-exp-form-vendor" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-[#6B6B7A]">Amount (pre-tax) *</label>
              <Input
                type="number" step="0.01" min="0"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0.00"
                className="h-9 rounded-xl border-[#E8E5DF] text-sm tabular-nums"
                data-testid="acct-exp-form-amount"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B6B7A]">HST (auto if blank)</label>
              <Input
                type="number" step="0.01" min="0"
                value={form.tax_amount}
                onChange={(e) => setForm({ ...form, tax_amount: e.target.value })}
                placeholder="auto = 13%"
                className="h-9 rounded-xl border-[#E8E5DF] text-sm tabular-nums"
                data-testid="acct-exp-form-tax"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-[#6B6B7A]">Payment Method</label>
              <select
                value={form.payment_method}
                onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                className="h-9 w-full rounded-xl border border-[#E8E5DF] text-sm bg-white px-3"
                data-testid="acct-exp-form-method"
              >
                {paymentMethods.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B6B7A]">Status</label>
              <div className="h-9 flex items-center gap-2">
                <label className="flex items-center gap-1 text-sm">
                  <input type="radio" checked={form.paid === true} onChange={() => setForm({ ...form, paid: true })} data-testid="acct-exp-form-paid" /> Paid
                </label>
                <label className="flex items-center gap-1 text-sm">
                  <input type="radio" checked={form.paid === false} onChange={() => setForm({ ...form, paid: false })} data-testid="acct-exp-form-unpaid" /> Unpaid (A/P)
                </label>
              </div>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#6B6B7A]">Receipt (PDF/JPG/PNG · max 5MB)</label>
            <div className="flex items-center gap-2">
              <label className="flex-1 cursor-pointer">
                <input
                  type="file" accept="application/pdf,image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => handleReceiptUpload(e.target.files?.[0])}
                  data-testid="acct-exp-form-receipt-input"
                />
                <div className="h-9 rounded-xl border border-dashed border-[#E8E5DF] text-sm text-[#6B6B7A] flex items-center justify-center hover:bg-[#FFFDF8]">
                  {uploading ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                  {form.receipt_url ? 'Replace receipt' : 'Upload receipt'}
                </div>
              </label>
              {form.receipt_url && (
                <Button type="button" variant="outline" size="sm" onClick={() => window.open(form.receipt_url, '_blank')} className="rounded-full text-xs border-[#E8E5DF]" data-testid="acct-exp-form-receipt-view">
                  <Eye className="w-3.5 h-3.5 mr-1" /> View
                </Button>
              )}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#6B6B7A]">Notes (optional)</label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any additional context…" className="rounded-xl border-[#E8E5DF] text-sm" rows={2} data-testid="acct-exp-form-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="acct-exp-form-cancel">Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
            data-testid="acct-exp-form-save"
          >
            <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving…' : 'Save Expense'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ────────  HST Summary Tab  ────────
const HstTab = ({ period, customStart, customEnd, setPeriod, setCustomStart, setCustomEnd }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHst = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === 'custom') { params.start = customStart; params.end = customEnd; }
      const res = await axios.get(`${API_URL}/api/admin/accounting/hst-summary`, { params, withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load HST summary');
    } finally {
      setLoading(false);
    }
  }, [period, customStart, customEnd]);

  useEffect(() => { fetchHst(); }, [fetchHst]);

  const handleExport = async (format) => {
    try {
      const params = { format, period };
      if (period === 'custom') { params.start = customStart; params.end = customEnd; }
      const res = await axios.get(`${API_URL}/api/admin/accounting/hst-summary/export`, {
        params, withCredentials: true, responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: format === 'pdf' ? 'application/pdf' : 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `HST_Return_${data?.period?.label || 'report'}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`HST return exported as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PeriodPicker
          period={period} setPeriod={setPeriod}
          customStart={customStart} setCustomStart={setCustomStart}
          customEnd={customEnd} setCustomEnd={setCustomEnd}
          label={data?.period?.label}
        />
        <div className="flex items-center gap-2">
          <Button
            onClick={() => handleExport('csv')} disabled={loading} variant="outline" size="sm"
            className="rounded-full text-xs border-[#E8E5DF]" data-testid="acct-hst-export-csv"
          >
            <Download className="w-3.5 h-3.5 mr-1" /> CSV
          </Button>
          <Button
            onClick={() => handleExport('pdf')} disabled={loading} size="sm"
            className="rounded-full text-xs bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="acct-hst-export-pdf"
          >
            <Download className="w-3.5 h-3.5 mr-1" /> PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* HST Collected */}
        <div className="bg-green-50 border border-green-200 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-base font-bold text-green-800" style={{ fontFamily: 'Fredoka' }}>HST Collected</h3>
            <ArrowUpRight className="w-5 h-5 text-green-600" />
          </div>
          <div className="text-3xl font-bold text-green-700" style={{ fontFamily: 'Fredoka' }}>{fmtMoney(data?.collected?.total)}</div>
          <div className="text-xs text-green-700 mt-2 space-y-0.5">
            <div className="flex justify-between"><span>From bookings</span><span>{fmtMoney(data?.collected?.bookings)}</span></div>
            <div className="flex justify-between"><span>From manual revenue</span><span>{fmtMoney(data?.collected?.manual)}</span></div>
            <div className="flex justify-between"><span>From POS / walk-in</span><span>{fmtMoney(data?.collected?.pos)}</span></div>
            <div className="flex justify-between font-semibold pt-1 border-t border-green-300/40 mt-1"><span>Taxable revenue</span><span>{fmtMoney(data?.collected?.taxable_revenue)}</span></div>
          </div>
        </div>

        {/* HST Paid (Input Tax Credits) */}
        <div className="bg-orange-50 border border-orange-200 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-base font-bold text-orange-800" style={{ fontFamily: 'Fredoka' }}>HST Paid (ITCs)</h3>
            <ArrowDownRight className="w-5 h-5 text-orange-600" />
          </div>
          <div className="text-3xl font-bold text-orange-700" style={{ fontFamily: 'Fredoka' }}>{fmtMoney(data?.paid?.total)}</div>
          <div className="text-xs text-orange-700 mt-2">
            <div className="flex justify-between"><span>Taxable expenses</span><span>{fmtMoney(data?.paid?.taxable_expenses)}</span></div>
          </div>
        </div>

        {/* Net HST */}
        <div className={`rounded-2xl p-5 border ${data?.is_refundable ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-center justify-between mb-2">
            <h3 className={`text-base font-bold ${data?.is_refundable ? 'text-blue-800' : 'text-red-800'}`} style={{ fontFamily: 'Fredoka' }}>
              Net HST {data?.is_refundable ? 'Refundable' : 'Payable'}
            </h3>
            <Calculator className={`w-5 h-5 ${data?.is_refundable ? 'text-blue-600' : 'text-red-600'}`} />
          </div>
          <div
            className="text-3xl font-bold"
            style={{ fontFamily: 'Fredoka', color: data?.is_refundable ? '#1D5BBF' : '#D4302A' }}
            data-testid="acct-hst-net"
          >
            {fmtMoney(Math.abs(data?.net || 0))}
          </div>
          <div className={`text-xs mt-2 ${data?.is_refundable ? 'text-blue-700' : 'text-red-700'}`}>
            {data?.is_refundable
              ? 'CRA owes you a refund'
              : data?.net === 0 ? 'No HST balance' : 'Remit this amount to CRA'}
            <div className="mt-1">Rate: {((data?.hst_rate || 0) * 100).toFixed(1)}%</div>
          </div>
        </div>
      </div>

      {/* Per-category breakdown of HST paid */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5">
        <h3 className="text-base font-bold text-[#1E1E24] mb-3" style={{ fontFamily: 'Fredoka' }}>
          HST Paid by Expense Category — {data?.period?.label}
        </h3>
        {loading ? (
          <div className="py-10 flex justify-center"><RefreshCw className="w-5 h-5 animate-spin text-[#FF3B6B]" /></div>
        ) : data?.paid?.breakdown?.length > 0 ? (
          <table className="w-full text-sm" data-testid="acct-hst-breakdown">
            <thead className="text-[11px] uppercase tracking-wider text-[#6B6B7A]">
              <tr>
                <th className="text-left py-2">Category</th>
                <th className="text-right py-2">Pre-tax</th>
                <th className="text-right py-2">HST</th>
                <th className="text-right py-2">Total</th>
                <th className="text-right py-2">Entries</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0EDE6]">
              {data.paid.breakdown.map((c) => (
                <tr key={c.category}>
                  <td className="py-2">
                    {c.category} {c.is_cogs && <Badge variant="outline" className="text-[10px] ml-1">COGS</Badge>}
                  </td>
                  <td className="text-right tabular-nums py-2">{fmtMoney(c.amount)}</td>
                  <td className="text-right tabular-nums py-2 font-semibold text-orange-700">{fmtMoney(c.tax)}</td>
                  <td className="text-right tabular-nums py-2">{fmtMoney(c.total)}</td>
                  <td className="text-right tabular-nums py-2 text-[#6B6B7A] text-xs">{c.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-[#6B6B7A] italic">No HST paid in this period.</p>
        )}
      </div>
    </div>
  );
};

// ────────  Manual Revenue Tab  ────────
const RevenueTab = ({ period, customStart, customEnd, setPeriod, setCustomStart, setCustomEnd }) => {
  const [entries, setEntries] = useState([]);
  const [totals, setTotals] = useState({});
  const [categories, setCategories] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === 'custom') { params.start = customStart; params.end = customEnd; }
      const res = await axios.get(`${API_URL}/api/admin/accounting/revenue-entries`, { params, withCredentials: true });
      setEntries(res.data.rows || []);
      setTotals(res.data.totals || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load revenue entries');
    } finally {
      setLoading(false);
    }
  }, [period, customStart, customEnd]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);
  useEffect(() => {
    axios.get(`${API_URL}/api/admin/accounting/categories`, { withCredentials: true })
      .then((r) => {
        setCategories(r.data.revenue_categories || []);
        setPaymentMethods(r.data.payment_methods || []);
      })
      .catch(() => {});
  }, []);

  const handleDelete = async (id, label) => {
    if (!window.confirm(`Delete revenue entry "${label}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/accounting/revenue-entries/${id}`, { withCredentials: true });
      toast.success('Revenue entry deleted');
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PeriodPicker
          period={period} setPeriod={setPeriod}
          customStart={customStart} setCustomStart={setCustomStart}
          customEnd={customEnd} setCustomEnd={setCustomEnd}
        />
        <Button
          onClick={() => setEditing('new')}
          size="sm"
          className="rounded-full text-xs bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
          data-testid="acct-revenue-add"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Manual Revenue
        </Button>
      </div>
      <div className="bg-[#FFFDF8] border border-[#FFE8B5] rounded-2xl p-3 text-xs text-[#6B6B7A]">
        <strong>Note:</strong> Booking revenue is auto-tracked from completed payments. This section is for revenue NOT tied to a booking — venue rental, consulting, merchandise, etc.
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <SummaryCard icon={DollarSign} label="Manual Revenue" value={fmtMoney(totals.amount)} sub="pre-tax" color="#0FAB66" testId="acct-rev-total" />
        <SummaryCard icon={Calculator} label="HST Collected" value={fmtMoney(totals.tax)} sub="from manual entries" color="#FF7A2E" testId="acct-rev-hst" />
        <SummaryCard icon={Wallet} label="Grand Total" value={fmtMoney(totals.grand_total)} sub="incl. tax" color="#9333EA" testId="acct-rev-grand" />
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center"><RefreshCw className="w-6 h-6 animate-spin text-[#FF3B6B]" /></div>
        ) : entries.length === 0 ? (
          <div className="py-16 text-center">
            <DollarSign className="w-10 h-10 text-[#E8E5DF] mx-auto mb-3" />
            <p className="text-sm text-[#6B6B7A] mb-3">No manual revenue entries in this period.</p>
            <Button
              onClick={() => setEditing('new')}
              size="sm"
              className="rounded-full text-xs bg-[#FF3B6B] hover:bg-[#E62E58] text-white"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add your first entry
            </Button>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="acct-rev-table">
            <thead className="bg-[#FFFDF8] text-[11px] uppercase tracking-wider text-[#6B6B7A]">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Date</th>
                <th className="text-left px-4 py-2 font-semibold">Description</th>
                <th className="text-left px-4 py-2 font-semibold">Customer</th>
                <th className="text-left px-4 py-2 font-semibold">Category</th>
                <th className="text-right px-4 py-2 font-semibold">Amount</th>
                <th className="text-right px-4 py-2 font-semibold">HST</th>
                <th className="text-right px-4 py-2 font-semibold">Total</th>
                <th className="text-right px-4 py-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0EDE6]">
              {entries.map((e) => (
                <tr key={e.entry_id} className="hover:bg-[#FFFDF8]" data-testid={`acct-rev-row-${e.entry_id}`}>
                  <td className="px-4 py-2 text-xs tabular-nums">{e.date}</td>
                  <td className="px-4 py-2 font-medium text-[#1E1E24]">{e.description}</td>
                  <td className="px-4 py-2 text-xs text-[#6B6B7A]">{e.customer_name || '—'}</td>
                  <td className="px-4 py-2">
                    <Badge variant="outline" className="text-[10px]">{e.category}</Badge>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{fmtMoney(e.amount)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-[#6B6B7A]">{fmtMoney(e.tax_amount)}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-semibold">{fmtMoney(e.total)}</td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button type="button" onClick={() => setEditing(e)} className="p-1.5 rounded-lg text-[#6B6B7A] hover:bg-[#FFFDF8] hover:text-[#FF3B6B]" title="Edit" data-testid={`acct-rev-edit-${e.entry_id}`}>
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => handleDelete(e.entry_id, e.description)} className="p-1.5 rounded-lg text-[#6B6B7A] hover:bg-red-50 hover:text-red-600" title="Delete" data-testid={`acct-rev-delete-${e.entry_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <RevenueDialog
          mode={editing === 'new' ? 'new' : 'edit'}
          initial={editing === 'new' ? null : editing}
          categories={categories}
          paymentMethods={paymentMethods}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchEntries(); }}
        />
      )}
    </div>
  );
};

const RevenueDialog = ({ mode, initial, categories, paymentMethods, onClose, onSaved }) => {
  const [form, setForm] = useState({
    date: initial?.date || new Date().toISOString().slice(0, 10),
    amount: initial?.amount ?? '',
    tax_amount: initial?.tax_amount ?? '',
    category: initial?.category || (categories[0] || 'Other'),
    customer_name: initial?.customer_name || '',
    description: initial?.description || '',
    payment_method: initial?.payment_method || 'cash',
    notes: initial?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    if (!form.amount || Number(form.amount) < 0) { toast.error('Amount must be ≥ 0'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: Number(form.amount),
        tax_amount: form.tax_amount === '' ? null : Number(form.tax_amount),
      };
      if (mode === 'new') {
        await axios.post(`${API_URL}/api/admin/accounting/revenue-entries`, payload, { withCredentials: true });
        toast.success('Revenue entry created');
      } else {
        await axios.put(`${API_URL}/api/admin/accounting/revenue-entries/${initial.entry_id}`, payload, { withCredentials: true });
        toast.success('Revenue entry updated');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg" data-testid="acct-revenue-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Fredoka' }}>
            {mode === 'new' ? 'Add Manual Revenue' : 'Edit Manual Revenue'}
          </DialogTitle>
          <DialogDescription>For revenue NOT tied to a booking (venue rental, consulting, etc.)</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-[#6B6B7A]">Date *</label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-9 rounded-xl border-[#E8E5DF] text-sm" data-testid="acct-rev-form-date" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B6B7A]">Category *</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="h-9 w-full rounded-xl border border-[#E8E5DF] text-sm bg-white px-3" data-testid="acct-rev-form-category">
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#6B6B7A]">Description *</label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Venue rental for community event" className="h-9 rounded-xl border-[#E8E5DF] text-sm" data-testid="acct-rev-form-desc" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#6B6B7A]">Customer Name</label>
            <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="e.g. Sara Martins" className="h-9 rounded-xl border-[#E8E5DF] text-sm" data-testid="acct-rev-form-customer" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-[#6B6B7A]">Amount (pre-tax) *</label>
              <Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="h-9 rounded-xl border-[#E8E5DF] text-sm tabular-nums" data-testid="acct-rev-form-amount" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B6B7A]">HST (auto if blank)</label>
              <Input type="number" step="0.01" min="0" value={form.tax_amount} onChange={(e) => setForm({ ...form, tax_amount: e.target.value })} placeholder="auto = 13%" className="h-9 rounded-xl border-[#E8E5DF] text-sm tabular-nums" data-testid="acct-rev-form-tax" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#6B6B7A]">Payment Method</label>
            <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className="h-9 w-full rounded-xl border border-[#E8E5DF] text-sm bg-white px-3" data-testid="acct-rev-form-method">
              {paymentMethods.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#6B6B7A]">Notes</label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl border-[#E8E5DF] text-sm" rows={2} data-testid="acct-rev-form-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="acct-rev-form-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="acct-rev-form-save">
            <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ────────  Settings Tab  ────────
const SettingsTab = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/accounting/settings`, { withCredentials: true });
      setSettings(res.data);
      setForm({
        hst_rate: res.data.hst_rate,
        cogs_categories: (res.data.cogs_categories || []).join(', '),
        opening_equity: res.data.opening_equity,
        opening_equity_date: res.data.opening_equity_date,
        business_name: res.data.business_name || '',
        business_address: res.data.business_address || '',
        business_hst_number: res.data.business_hst_number || '',
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        hst_rate: Number(form.hst_rate),
        opening_equity: Number(form.opening_equity),
        cogs_categories: form.cogs_categories.split(',').map((s) => s.trim()).filter(Boolean),
      };
      await axios.put(`${API_URL}/api/admin/accounting/settings`, payload, { withCredentials: true });
      toast.success('Settings saved');
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-16 flex justify-center"><RefreshCw className="w-6 h-6 animate-spin text-[#FF3B6B]" /></div>;

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5">
        <h3 className="text-lg font-bold text-[#1E1E24] mb-4" style={{ fontFamily: 'Fredoka' }}>Accounting Settings</h3>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-[#6B6B7A]">HST/GST Rate (decimal, e.g. 0.13 for 13%) *</label>
            <Input type="number" step="0.0001" min="0" max="1" value={form.hst_rate} onChange={(e) => setForm({ ...form, hst_rate: e.target.value })} className="h-9 rounded-xl border-[#E8E5DF] text-sm tabular-nums w-40" data-testid="acct-settings-hst-rate" />
            <p className="text-[11px] text-[#6B6B7A] mt-1">Ontario uses 0.13 (13%). Quebec uses 0.05 + 0.09975 (GST + QST). Update if you operate elsewhere.</p>
          </div>

          <div>
            <label className="text-xs font-semibold text-[#6B6B7A]">COGS Categories (comma-separated)</label>
            <Input value={form.cogs_categories} onChange={(e) => setForm({ ...form, cogs_categories: e.target.value })} placeholder="COGS, Supplies, Materials" className="h-9 rounded-xl border-[#E8E5DF] text-sm" data-testid="acct-settings-cogs-cats" />
            <p className="text-[11px] text-[#6B6B7A] mt-1">Expense categories matching these names are classified as COGS (Cost of Goods Sold) in P&L reports.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-[#6B6B7A]">Opening Equity</label>
              <Input type="number" step="0.01" value={form.opening_equity} onChange={(e) => setForm({ ...form, opening_equity: e.target.value })} className="h-9 rounded-xl border-[#E8E5DF] text-sm tabular-nums" data-testid="acct-settings-opening-equity" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B6B7A]">Opening Equity Date</label>
              <Input type="date" value={form.opening_equity_date} onChange={(e) => setForm({ ...form, opening_equity_date: e.target.value })} className="h-9 rounded-xl border-[#E8E5DF] text-sm" data-testid="acct-settings-opening-equity-date" />
            </div>
          </div>

          <div className="pt-3 border-t border-[#E8E5DF]">
            <h4 className="text-sm font-bold text-[#1E1E24] mb-2">Business Info (for PDF exports)</h4>
            <div className="space-y-2">
              <div>
                <label className="text-xs font-semibold text-[#6B6B7A]">Business Name</label>
                <Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="h-9 rounded-xl border-[#E8E5DF] text-sm" data-testid="acct-settings-business-name" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B6B7A]">Business Address</label>
                <Textarea value={form.business_address} onChange={(e) => setForm({ ...form, business_address: e.target.value })} rows={2} className="rounded-xl border-[#E8E5DF] text-sm" data-testid="acct-settings-business-address" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B6B7A]">HST/GST Number</label>
                <Input value={form.business_hst_number} onChange={(e) => setForm({ ...form, business_hst_number: e.target.value })} placeholder="e.g. 123456789 RT0001" className="h-9 rounded-xl border-[#E8E5DF] text-sm" data-testid="acct-settings-business-hst" />
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button onClick={handleSave} disabled={saving} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="acct-settings-save">
              <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving…' : 'Save Settings'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ────────  Main Accounting Page  ────────
export const CrmAccounting = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [period, setPeriod] = useState('current_month');
  const [customStart, setCustomStart] = useState(new Date().toISOString().slice(0, 10));
  const [customEnd, setCustomEnd] = useState(new Date().toISOString().slice(0, 10));

  const sharedProps = { period, customStart, customEnd, setPeriod, setCustomStart, setCustomEnd };

  return (
    <div className="space-y-5 max-w-7xl" data-testid="crm-accounting">
      <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Accounting</h1>
        <p className="text-[#6B6B7A] text-sm mt-1">
          Standalone finance — Sales, Expenses, P&L, Balance Sheet, HST/GST. No external software needed.
        </p>
      </motion.div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border border-[#E8E5DF] rounded-2xl p-1 flex flex-wrap h-auto" data-testid="acct-tabs">
          <TabsTrigger value="dashboard" className="rounded-xl data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white text-sm" data-testid="acct-tab-dashboard">
            <BarChart3 className="w-4 h-4 mr-1.5" /> Dashboard
          </TabsTrigger>
          <TabsTrigger value="pnl" className="rounded-xl data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white text-sm" data-testid="acct-tab-pnl">
            <TrendingUp className="w-4 h-4 mr-1.5" /> P&L
          </TabsTrigger>
          <TabsTrigger value="balance-sheet" className="rounded-xl data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white text-sm" data-testid="acct-tab-balance-sheet">
            <BookOpen className="w-4 h-4 mr-1.5" /> Balance Sheet
          </TabsTrigger>
          <TabsTrigger value="expenses" className="rounded-xl data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white text-sm" data-testid="acct-tab-expenses">
            <Receipt className="w-4 h-4 mr-1.5" /> Expenses
          </TabsTrigger>
          <TabsTrigger value="revenue" className="rounded-xl data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white text-sm" data-testid="acct-tab-revenue">
            <DollarSign className="w-4 h-4 mr-1.5" /> Manual Revenue
          </TabsTrigger>
          <TabsTrigger value="hst" className="rounded-xl data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white text-sm" data-testid="acct-tab-hst">
            <Calculator className="w-4 h-4 mr-1.5" /> HST/GST
          </TabsTrigger>
          <TabsTrigger value="settings" className="rounded-xl data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white text-sm" data-testid="acct-tab-settings">
            <Settings className="w-4 h-4 mr-1.5" /> Settings
          </TabsTrigger>
        </TabsList>

        <div className="mt-5">
          <TabsContent value="dashboard"><DashboardTab {...sharedProps} /></TabsContent>
          <TabsContent value="pnl"><PnlTab {...sharedProps} /></TabsContent>
          <TabsContent value="balance-sheet"><BalanceSheetTab /></TabsContent>
          <TabsContent value="expenses"><ExpensesTab {...sharedProps} /></TabsContent>
          <TabsContent value="revenue"><RevenueTab {...sharedProps} /></TabsContent>
          <TabsContent value="hst"><HstTab {...sharedProps} /></TabsContent>
          <TabsContent value="settings"><SettingsTab /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default CrmAccounting;
